import React from 'react';
import moment from 'moment-timezone';
import { Chip } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { formatDueDate, getStageColor } from '../../../pages/task-manager/task-manager.constants';

/**
 * CIN-7048: how the customer drawer shows a task's status.
 *
 * The backend decides the status (cinch_task_manager/timeline.py task_status) and
 * sends one of the four keys below. This file only maps a key to its look, so the
 * Timeline and the Details tab can never color the same task differently.
 *
 * Icon colors are the exact hex values the Timeline already uses for email, text
 * and call status (blue #3898D9, green #3EB87B, red #EF3C34). Orange is Cinch's
 * action orange. Pills reuse the curated STAGE_COLORS pairs.
 */
export const TASK_STATUS = {
  open: {
    key: 'open',
    label: 'Open',
    color: '#3898D9',
    chip: getStageColor('blue')
  },
  // The pill shares its orange pair with DueChip's "Due today", so an on-time card
  // due today shows an orange due chip under a blue Open pill (plan DC14).
  stage_overdue: {
    key: 'stage_overdue',
    label: 'Stage overdue',
    color: '#E77A16',
    chip: getStageColor('orange')
  },
  overdue: {
    key: 'overdue',
    label: 'Overdue',
    color: '#EF3C34',
    chip: getStageColor('red')
  },
  completed: {
    key: 'completed',
    label: 'Completed',
    color: '#3EB87B',
    chip: getStageColor('green')
  }
};

// Most urgent first: the order of the Details tab's cards and its dot strip. The
// Timeline's Event filter lists the statuses in lifecycle order instead.
export const TASK_STATUS_ORDER = ['overdue', 'stage_overdue', 'open', 'completed'];
export const getTaskStatus = status => TASK_STATUS[status] || TASK_STATUS.open;

// CIN-7048 (plan D12): option A from cin-7047-7048-task-status-icons-mockup.html,
// a box with a checkmark. Swap these two for option B (FactCheckIcon /
// FactCheckFilledIcon) or C (AssignmentTurnedInOutlined / AssignmentTurnedIn);
// every task glyph in the drawer comes from here, including the Type filter's.
// The filled glyph marks an expanded Timeline row, like the email and text rows.
const OutlinedGlyph = CheckBoxOutlinedIcon;
const FilledGlyph = CheckBoxIcon;

// The uncolored glyph for the Timeline's Type filter menu.
export const TaskTypeIcon = () => <OutlinedGlyph sx={{
  fontSize: '22px'
}} />;
export const TaskStatusIcon = ({
  status,
  filled = false,
  fontSize = 22
}) => {
  const Glyph = filled ? FilledGlyph : OutlinedGlyph;
  const {
    key,
    color
  } = getTaskStatus(status);
  return <Glyph data-status={key} sx={{
    color,
    fontSize
  }} />;
};
export const TaskStatusPill = ({
  status
}) => {
  const {
    label,
    chip
  } = getTaskStatus(status);
  return <Chip size="small" label={label} data-testid="task-status-pill" sx={{
    backgroundColor: chip.bg,
    color: chip.text,
    fontWeight: 600,
    borderRadius: '100px'
  }} />;
};
const pluralDays = days => `${days} ${days === 1 ? 'day' : 'days'}`;

// Days since the task entered its current stage, counted in calendar days. These
// are browser-local days while stage_due_date is company-local, so near midnight
// the count can be one off from the deadline shown beside it (display only).
const daysInStage = task => task.stage_entered_when ? moment().startOf('day').diff(moment(task.stage_entered_when).startOf('day'), 'days') : null;

/** One sentence that explains the color, for the Details card and the expanded Timeline row. */
export const describeTaskStatus = task => {
  if (task.status === 'completed') {
    if (!task.completed_when) return 'In its completion stage.';
    const by = task.completed_by_name ? ` by ${task.completed_by_name}` : '';
    return `Completed ${moment(task.completed_when).format('MMM D, YYYY')}${by}.`;
  }
  if (task.status === 'overdue') {
    return `Past its due date of ${formatDueDate(task.due_date)}.`;
  }
  if (task.status === 'stage_overdue') {
    const days = daysInStage(task);
    const inStage = days === null ? `In ${task.stage_name}.` : `In ${task.stage_name} for ${pluralDays(days)}.`;
    const due = task.due_date ? ` The task itself is due ${formatDueDate(task.due_date)}.` : '';
    return `${inStage} Its stage deadline was ${formatDueDate(task.stage_due_date)}.${due}`;
  }
  return task.due_date ? `On time. Due ${formatDueDate(task.due_date)}.` : 'On time.';
};

/** Hover text for the Timeline icon, e.g. "Task – Overdue · due Sep 25". */
export const taskStatusTooltip = task => {
  const {
    label
  } = getTaskStatus(task.status);
  if (task.status === 'stage_overdue') return `Task – ${label} · stage deadline ${formatDueDate(task.stage_due_date)}`;
  if (task.status !== 'completed' && task.due_date) return `Task – ${label} · due ${formatDueDate(task.due_date)}`;
  return `Task – ${label}`;
};

// A missing or unknown status ranks as Open, the way getTaskStatus draws it, instead
// of sorting ahead of every overdue task.
const statusRank = status => TASK_STATUS_ORDER.indexOf(getTaskStatus(status).key);

/** Details-tab order: most urgent status first, then the most recently updated. */
export const sortTasksForDetails = tasks => [...(tasks || [])].sort((a, b) => {
  const byStatus = statusRank(a.status) - statusRank(b.status);
  if (byStatus !== 0) return byStatus;
  return (b.updated_when || '').localeCompare(a.updated_when || '');
});